import * as React from 'react';
import {
    Dialog,
    DialogType,
    DialogFooter
} from 'office-ui-fabric-react/lib/Dialog';
import {
    DefaultButton,
    PrimaryButton
} from 'office-ui-fabric-react/lib/Button';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import strings from '../../../Share.resx';
import './ConfirmationDialog.scss';

export enum ConfirmationCode {
    none,
    link,
    participant,
    linkSettings,
    back,
    shareDialogPasswordOnly,
    shareDialogExpirationOnly,
    shareDialogPasswordAndExpiration,
    copyInterruptUpdate,
    copyInterruptRemove
}
export interface IConfirmationDialogProps {
    confirmationCode: ConfirmationCode;
    participant: IPerson;
    participants?: IPerson[];
    onApply: () => void;
    onCancel: () => void;
    onDiscard?: () => void;
    linkPasswordSetBy?: string;
    linkExpiration?: string;
}

export interface IConfirmationDialogState {
}

export class ConfirmationDialog extends React.Component<IConfirmationDialogProps, IConfirmationDialogState> {

    constructor(props: IConfirmationDialogProps, context: any) {
        super(props);
    }

    public render(): React.ReactElement<{}> {
        const { confirmationCode, participant } = this.props;
        let title: string;
        let applyButtonText: string;
        let cancelButtonText: string;
        //Special CSS formatting for changing password/expiration in the share dialog
        let dialogClassName = "od-confirmationDialog";

        switch (confirmationCode) {
            case ConfirmationCode.link:
                title = strings.deleteLinkDialogTitle;
                applyButtonText = strings.deleteLinkButtonText;
                cancelButtonText = strings.cancelButtonText;
                break;
            case ConfirmationCode.linkSettings:
                title = strings.changeLinkSettingsDialogTitle;
                applyButtonText = strings.changeItButtonText;
                cancelButtonText = strings.cancelButtonText;
                break;
            case ConfirmationCode.participant:
                title = StringHelper.format(strings.deleteLinkParticipantDialogTitle, participant.name);
                applyButtonText = strings.removeButtonText;
                cancelButtonText = strings.cancelButtonText;
                break;
            case ConfirmationCode.back:
                title = strings.leaveLinkSettingsDialogTitle;
                applyButtonText = strings.saveButtonText;
                cancelButtonText = strings.cancelButtonText;
                break;
            case ConfirmationCode.shareDialogPasswordOnly:
                title = strings.updateLinkDialogTitle;
                applyButtonText = strings.useNewPasswordText;
                cancelButtonText = strings.useExistingPasswordText;
                dialogClassName += " shareDialog";
                break;
            case ConfirmationCode.shareDialogExpirationOnly:
                title = strings.updateLinkDialogTitle;
                applyButtonText = strings.useNewExpirationText;
                cancelButtonText = strings.useExistingExpirationText;
                dialogClassName += " shareDialog";
                break;
            case ConfirmationCode.shareDialogPasswordAndExpiration:
                title = strings.updateLinkDialogTitle;
                applyButtonText = strings.useNewSettingsText;
                cancelButtonText = strings.useExistingSettingsText;
                dialogClassName += " shareDialog";
                break;
            case ConfirmationCode.copyInterruptUpdate:
                title = strings.copyInterruptUpdateTitle;
                applyButtonText = strings.copyInterruptContinue;
                cancelButtonText = strings.copyInterruptOldPassword;
                dialogClassName += " shareDialog";
                break;
            case ConfirmationCode.copyInterruptRemove:
                title = strings.copyInterruptRemoveTitle;
                applyButtonText = strings.copyInterruptContinue;
                cancelButtonText = strings.copyInterruptRemovePassword;
                dialogClassName += " shareDialog";
                break;
        }

        return (
            <div>
                <Dialog
                    hidden={ false }
                    className={ dialogClassName }
                    onDismiss={ this.props.onCancel }
                    dialogContentProps={ {
                        type: DialogType.close,
                        title: title,
                    } }
                    modalProps={ {
                        isDarkOverlay: true,
                        isBlocking: true
                    } }
                >
                    { this._getDialogSubText() }
                    { confirmationCode === ConfirmationCode.back ?
                        <DialogFooter className="od-confirmationDialog-footer">
                            <DefaultButton className="od-confirmationDialog-discard" onClick={ this.props.onDiscard ? this.props.onDiscard : this.props.onCancel } text={ strings.discardButtonText } />
                            <div className="od-confirmationDialog-saveCancel">
                                <PrimaryButton onClick={ this.props.onApply } text={ applyButtonText } />
                                <DefaultButton onClick={ this.props.onCancel } text={ cancelButtonText } />
                            </div>
                        </DialogFooter> :
                        (confirmationCode === ConfirmationCode.copyInterruptUpdate || confirmationCode === ConfirmationCode.copyInterruptRemove) ?
                            <DialogFooter>
                                <PrimaryButton className='od-ConfirmationDialog-copyLinkInterruptButtons' onClick={ this.props.onApply } text={ applyButtonText } />
                                <DefaultButton className='od-ConfirmationDialog-copyLinkInterruptButtons' onClick={ this.props.onDiscard || this.props.onCancel } text={ cancelButtonText } />
                            </DialogFooter> :
                            <DialogFooter>
                                <PrimaryButton onClick={ this.props.onApply } text={ applyButtonText } />
                                <DefaultButton onClick={ this.props.onCancel } text={ cancelButtonText } />
                            </DialogFooter>
                    }
                </Dialog>
            </div>
        );
    }

    private _getDialogSubText(): JSX.Element {
        const { confirmationCode } = this.props;
        let subText = "";
        switch (confirmationCode) {
            case ConfirmationCode.link:
                return (
                    <div className='od-ConfirmationDialog-subText'>
                        <span><strong>{ strings.deleteLinkLeadText }</strong></span>
                        <span>{ strings.deleteLinkEndText }</span>
                    </div>
                );
            case ConfirmationCode.linkSettings:
                return (
                    <div className='od-ConfirmationDialog-subText'>
                        <span>{ strings.applyLinkSettingsText }</span>
                    </div>
                );
            case ConfirmationCode.participant:
                return undefined;
            case ConfirmationCode.shareDialogPasswordOnly:
                subText = StringHelper.format(strings.overwritePasswordText, this.props.linkPasswordSetBy);
                return (
                    <div className='od-ConfirmationDialog-subText' role='alert'>
                        <span>{ subText }</span>
                    </div>
                );
            case ConfirmationCode.shareDialogExpirationOnly:
                subText = StringHelper.format(strings.overwriteExpirationText, this.props.linkExpiration);
                return (
                    <div className='od-ConfirmationDialog-subText' role='alert'>
                        <span>{ subText }</span>
                    </div>
                );
            case ConfirmationCode.shareDialogPasswordAndExpiration:
                subText = StringHelper.format(strings.overwriteSettingsText, this.props.linkExpiration, this.props.linkPasswordSetBy);
                return (
                    <div className='od-ConfirmationDialog-subText' role='alert'>
                        <span>{ subText }</span>
                    </div>
                );
            case ConfirmationCode.copyInterruptUpdate:
                subText = strings.copyInterruptUpdateContent;
                return (
                    <div className='od-ConfirmationDialog-subText' role='alert'>
                        <span>{ subText }</span>
                    </div>
                );
            case ConfirmationCode.copyInterruptRemove:
                subText = strings.copyInterruptRemoveContent;
                return (
                    <div className='od-ConfirmationDialog-subText' role='alert'>
                        <span>{ subText }</span>
                    </div>
                );
        }
    }
}